import React from 'react';
import styled from 'styled-components';
import get from 'lodash/get';
import { cta2FontWeight, screenMdMin, screenSmMax, screenXsMax, spacer16, spacer24, spacer32, spacer48, spacer8, Type } from '@spotify-internal/encore-web';
import { Link, ButtonTertiaryAsLink, withAnimationContext, DELAYS, fadeInAnimation } from '../../ui';
import { fixHref, formatText, getBurst, transformScaleUp } from '../../utils';
import androidIcon from '../../Layout/images/footer-android.svg';
import iosIcon from '../../Layout/images/footer-ios.svg';
import { useT } from '@mrkt/features/i18n';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var TRACK_CAT = 'module-pageBreaker';
var PageBreaker = styled.div.withConfig({
  displayName: "PageBreakerModule__PageBreaker",
  componentId: "hui7a1-0"
})(["display:flex;align-items:center;min-height:236px;", ";@media (max-width:", "){flex-direction:column;justify-content:center;min-height:auto;background-image:none;}"], function (props) {
  return props.showBurst ? getBurst(props.theme.box.text) : '';
}, screenSmMax);
var Left = styled.div.withConfig({
  displayName: "PageBreakerModule__Left",
  componentId: "hui7a1-1"
})(["width:100%;padding-top:", ";padding-bottom:", ";@media (max-width:", "){text-align:left;}"], spacer16, spacer32, screenSmMax);
var Right = styled.div.withConfig({
  displayName: "PageBreakerModule__Right",
  componentId: "hui7a1-2"
})(["width:100%;padding-top:", ";padding-bottom:", ";@media (max-width:", "){padding-top:0px;padding-bottom:", ";align-items:center;}"], spacer32, spacer32, screenSmMax, spacer16);
var Headline = withAnimationContext(styled(Type.h1).attrs({
  variant: 'heading1'
}).withConfig({
  displayName: "PageBreakerModule__Headline",
  componentId: "hui7a1-3"
})(["", ";font-weight:", ";padding-bottom:0;"], fadeInAnimation(DELAYS.SECOND), cta2FontWeight));
var SubtextContainer = withAnimationContext(styled.div.attrs({
  variant: 'body1'
}).withConfig({
  displayName: "PageBreakerModule__SubtextContainer",
  componentId: "hui7a1-4"
})(["", ";padding-top:", ";padding-bottom:", ";@media (max-width:", "){padding-bottom:0;}"], fadeInAnimation(DELAYS.THIRD), spacer8, spacer8, screenSmMax));
var StyledImage = styled.img.withConfig({
  displayName: "PageBreakerModule__StyledImage",
  componentId: "hui7a1-5"
})(["@media (min-width:", "){padding-right:", ";padding-left:", ";}@media (max-width:", "){padding-right:", ";}"], screenMdMin, spacer24, spacer24, screenSmMax, spacer8);
var CTAContainer = styled.div.withConfig({
  displayName: "PageBreakerModule__CTAContainer",
  componentId: "hui7a1-6"
})(["display:flex;justify-content:flex-end;padding-right:", ";@media (max-width:", "){justify-content:initial;}@media (max-width:", "){padding-top:0px;padding-bottom:", ";padding-right:0;justify-content:center;}"], spacer48, screenSmMax, screenXsMax, spacer24);
var CTABtn = styled(ButtonTertiaryAsLink).withConfig({
  displayName: "PageBreakerModule__CTABtn",
  componentId: "hui7a1-7"
})(["&&{background-color:", ";color:", ";}&:hover:not(:focus):not(:disabled){color:", ";text-decoration:none;", ";}&:focus:not(:disabled){color:", ";outline:thin dotted black;}@media (max-width:", "){&:hover:not(:focus):not(:disabled){transform:none;}}"], function (_ref) {
  var theme = _ref.theme;
  return theme.cta.background;
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.cta.text;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.cta.text;
}, transformScaleUp, function (_ref4) {
  var theme = _ref4.theme;
  return theme.cta.text;
}, screenSmMax);

var AppStores = function AppStores() {
  var t = useT();
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Link, {
      external: true,
      href: "https://itunes.apple.com/us/app/spotify-for-artists/id1222021797",
      track: [TRACK_CAT, "CTA App Store Link - iOS"],
      children: /*#__PURE__*/_jsx(StyledImage, {
        src: iosIcon,
        alt: t('FRODOR_147246', 'Download banner for iOS', 'Banner refers to the visual element on the page')
      })
    }), /*#__PURE__*/_jsx(Link, {
      external: true,
      href: "https://play.google.com/store/apps/details?id=com.spotify.s4a",
      track: [TRACK_CAT, "CTA App Store Link - Android"],
      children: /*#__PURE__*/_jsx("img", {
        src: androidIcon,
        alt: t('FRODOR_6a8897', 'Download banner for Android', 'Banner refers to the visual element on the page')
      })
    })]
  });
};

var AppStoresContainer = styled.div.withConfig({
  displayName: "PageBreakerModule__AppStoresContainer",
  componentId: "hui7a1-8"
})(["display:flex;justify-content:flex-end;@media (max-width:", "){padding-top:", ";padding-bottom:", ";padding-left:", ";justify-content:flex-start;}a:focus img{", ";}"], screenSmMax, spacer16, spacer16, spacer16, transformScaleUp);

var getPageBreakerVersion = function getPageBreakerVersion(moduleVersion, ctaButton) {
  switch (moduleVersion) {
    case 'Call to Action':
      if (get(ctaButton, 'ctaText.length') > 0 && get(ctaButton, 'ctaLink.length') > 0) {
        return /*#__PURE__*/_jsx(CTAContainer, {
          children: /*#__PURE__*/_jsx(CTABtn, {
            href: fixHref(ctaButton.ctaLink),
            track: [TRACK_CAT, "CTA Link - ".concat(ctaButton.title)],
            children: ctaButton.ctaText
          })
        });
      }

      return /*#__PURE__*/_jsx(_Fragment, {});

    case 'App Stores':
      return /*#__PURE__*/_jsx(AppStoresContainer, {
        children: /*#__PURE__*/_jsx(AppStores, {})
      });

    default:
      return /*#__PURE__*/_jsx(_Fragment, {});
  }
};

export var PageBreakerModule = /*#__PURE__*/React.forwardRef(function (_ref5, ref) {
  var _ref5$headline = _ref5.headline,
      headline = _ref5$headline === void 0 ? '' : _ref5$headline,
      _ref5$subtext = _ref5.subtext,
      subtext = _ref5$subtext === void 0 ? '' : _ref5$subtext,
      _ref5$ctaButton = _ref5.ctaButton,
      ctaButton = _ref5$ctaButton === void 0 ? {
    ctaText: '',
    ctaLink: '',
    title: ''
  } : _ref5$ctaButton,
      _ref5$moduleVersion = _ref5.moduleVersion,
      moduleVersion = _ref5$moduleVersion === void 0 ? 'Call to Action' : _ref5$moduleVersion,
      _ref5$showBurst = _ref5.showBurst,
      showBurst = _ref5$showBurst === void 0 ? false : _ref5$showBurst;
  return /*#__PURE__*/_jsxs(PageBreaker, {
    ref: ref,
    hasSubtext: Boolean(subtext),
    showBurst: showBurst,
    children: [/*#__PURE__*/_jsxs(Left, {
      children: [headline && /*#__PURE__*/_jsx(Headline, {
        dangerouslySetInnerHTML: {
          __html: formatText(headline)
        }
      }), subtext && /*#__PURE__*/_jsx(SubtextContainer, {
        dangerouslySetInnerHTML: {
          __html: formatText(subtext)
        }
      })]
    }), /*#__PURE__*/_jsx(Right, {
      children: getPageBreakerVersion(moduleVersion, ctaButton)
    })]
  });
});